import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDoorOpen,
  faClockRotateLeft,
  faHeartPulse,
  faGaugeHigh,
  faFaceSmile,
  faSeedling,
  faStethoscope,
  faHandHoldingHeart,
  faFlaskVial,
  faHeart,
  faWandMagicSparkles,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import LifeNHealthHeaderBanner, { PRODUCTS } from "./LifeNHealthHeaderBanner";

/* ------------------------------------------------------------------ */
/*  Fixed data (routes never change)                                   */
/* ------------------------------------------------------------------ */
const EHSAAS_ITEMS = [
  {
    label: "PurpleDoor",
    desc: "Daily journal",
    icon: faDoorOpen,
    route: "/purpledoordashboard",
  },
  {
    label: "LifeReplay",
    desc: "Cherished moments",
    icon: faClockRotateLeft,
    route: "/lifereplaydashboard",
  },
  {
    label: "Pulse",
    desc: "Vitals check-in",
    icon: faHeartPulse,
    route: "/pulsedashboard",
  },
  {
    label: "ScorePlus",
    desc: "Wellness score",
    icon: faGaugeHigh,
    route: "/scoreplus",
  },
  {
    label: "Mood Ratings",
    desc: "How you feel",
    icon: faFaceSmile,
    route: "/fanratings",
  },
];

const GROWTH_ITEMS = [
  {
    label: "PurpleDoor",
    desc: "Daily journal",
    icon: faDoorOpen,
    route: "/purpledoordashboard",
  },
  {
    label: "LifeReplay",
    desc: "Milestone memories",
    icon: faClockRotateLeft,
    route: "/lifereplaydashboard",
  },
  {
    label: "Pulse",
    desc: "Vitals check-in",
    icon: faHeartPulse,
    route: "/pulsedashboard",
  },
  {
    label: "ScorePlus",
    desc: "Wellness score",
    icon: faGaugeHigh,
    route: "/scoreplus",
  },
  {
    label: "Mom & Kid Ratings",
    desc: "Shared moods",
    icon: faHeart,
    route: "/fanratings",
  },
  {
    label: "GrowthLoop",
    desc: "Growth milestones",
    icon: faSeedling,
    route: "/growthloop",
  },
];

/* Base CARE_NETWORK – routes stay exactly the same.
   Only role descriptions + chip labels are overridden per product. */
const BASE_CARE_NETWORK = [
  {
    role: "Doctor",
    icon: faStethoscope,
    desc: "A clinical view of every expecting mother in your care.",
    chips: [
      { label: "Patient", route: "/scoreplus-doctor" },
      // { label: "Child", route: "/growthloop" },
    ],
  },
  {
    role: "Health Advisor",
    icon: faHandHoldingHeart,
    desc: "Track and support the patients in your community.",
    chips: [
      { label: "Patient", route: "/scoreplus-health-advisor" },
      // { label: "Child", route: "/growthloop" },
    ],
  },
  {
    role: "Pharma Partner",
    icon: faFlaskVial,
    desc: "Aggregate insight across the maternity and early-childhood journey.",
    chips: [
      { label: "Patient", route: "/scoreplus-pharma" },
      // { label: "Early Childhood", route: "/growthloop" },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Product-specific copy (labels & descriptions only)                 */
/* ------------------------------------------------------------------ */
const PRODUCT_COPY = {
  maternity: {
    ehsaas: {
      emoji: "🌸",
      eyebrow: "LifeNHealth Ehsaas · Daily",
      title: "For the Mom-to-Be",
      subtitle:
        "A gentle daily check-in that holds her feelings, her body, and her story.",
    },
    growth: {
      emoji: "🌿",
      eyebrow: "LifeNHealth Growth · Daily",
      title: "For Mom & Kid",
      subtitle:
        "One thread that grows with her — through pregnancy and into every childhood milestone.",
    },
    care: {
      eyebrow: "LifeNHealth Care Network",
      title: "Everyone Around Her",
      subtitle:
        "The doctors, community advisors / workers, and partners who walk this journey alongside her.",
    },
    careNetwork: BASE_CARE_NETWORK, // original labels
    closingTitle: "You're doing amazing, Mom.",
    closingSub:
      "We're so proud of you — and we're keeping every step in LifeNHealth.",
  },

  childgrowth: {
    ehsaas: {
      emoji: "🌱",
      eyebrow: "Anvaya Growth · Daily",
      title: "Growing Together",
      subtitle:
        "Track milestones, moods, and the everyday magic of early childhood.",
    },
    growth: {
      emoji: "🌿",
      eyebrow: "LifeNHealth Growth · Daily",
      title: "For Mom & Kid",
      subtitle:
        "One thread that grows with her — through pregnancy and into every childhood milestone.",
    },
    care: {
      eyebrow: "LifeNHealth Care Network",
      title: "Everyone Around Them",
      subtitle:
        "The clinicians, community advisors / workers and partners supporting every child’s journey.",
    },
    careNetwork: [
      {
        role: "Doctor",
        icon: faStethoscope,
        desc: "A clinical view of every child in your care.",
        chips: [
          { label: "Children", route: "/scoreplus" },
          { label: "Growth", route: "/growthloop" },
        ],
      },
      {
        role: "Health Worker",
        icon: faHandHoldingHeart,
        desc: "Track and support the children and families in your community.",
        chips: [
          { label: "Children", route: "/scoreplus" },
          { label: "Growth", route: "/growthloop" },
        ],
      },
      {
        role: "Pharma Partner",
        icon: faFlaskVial,
        desc: "Aggregate insight across early-childhood development.",
        chips: [
          { label: "Child Audience", route: "/scoreplus" },
          { label: "Early Childhood", route: "/growthloop" },
        ],
      },
    ],
    closingTitle: "Every milestone matters.",
    closingSub:
      "We're walking this growth journey with you and your little one.",
  },

  // All other products share the same EHSAAS_ITEMS + adapted care labels
  balance: {
    ehsaas: {
      emoji: "🩸",
      eyebrow: "Balance · Daily",
      title: "Steady & Strong",
      subtitle:
        "Glucose, meals, mood and movement — all in one calm place.",
    },
    care: {
      eyebrow: "LifeNHealth Care Network",
      title: "Everyone Around You",
      subtitle:
        "The clinicians, coordinators and partners supporting your diabetes journey.",
    },
    careNetwork: [
      {
        role: "Doctor",
        icon: faStethoscope,
        desc: "A clinical view of every person managing diabetes in your care.",
        chips: [
          { label: "Diabetes Cohort", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
      {
        role: "Community Health Worker",
        icon: faHandHoldingHeart,
        desc: "Track and support people living with diabetes in your community.",
        chips: [
          { label: "Diabetes Cohort", route: "/scoreplus" },
          { label: "Support", route: "/growthloop" },
        ],
      },
      {
        role: "Pharma Partner",
        icon: faFlaskVial,
        desc: "Aggregate insight across the diabetes management journey.",
        chips: [
          { label: "Diabetes Audience", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
    ],
    closingTitle: "Small steady steps add up.",
    closingSub: "Balance is a practice — and we're here for every reading.",
  },

  vital: {
    ehsaas: {
      emoji: "❤️‍🔥",
      eyebrow: "Vital · Daily",
      title: "Heart First",
      subtitle: "Track the rhythm that powers everything else.",
    },
    care: {
      eyebrow: "LifeNHealth Care Network",
      title: "Everyone Around You",
      subtitle:
        "The clinicians and partners protecting heart health together.",
    },
    careNetwork: [
      {
        role: "Doctor",
        icon: faStethoscope,
        desc: "A clinical view of every cardiac patient in your care.",
        chips: [
          { label: "Cardiac Cohort", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
      {
        role: "Community Health Worker",
        icon: faHandHoldingHeart,
        desc: "Track and support heart-health journeys in your community.",
        chips: [
          { label: "Cardiac Cohort", route: "/scoreplus" },
          { label: "Support", route: "/growthloop" },
        ],
      },
      {
        role: "Pharma Partner",
        icon: faFlaskVial,
        desc: "Aggregate insight across the cardio care journey.",
        chips: [
          { label: "Cardio Audience", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
    ],
    closingTitle: "Your heart is in good hands.",
    closingSub: "We're watching the numbers so you can focus on living.",
  },

  herlife: {
    ehsaas: {
      emoji: "🌸",
      eyebrow: "HerLife · Daily",
      title: "For Her, At Every Age",
      subtitle:
        "Hormones, energy, mood and the quiet strength of womanhood after 30.",
    },
    care: {
      eyebrow: "LifeNHealth Care Network",
      title: "Everyone Around Her",
      subtitle:
        "The clinicians and partners supporting women’s health journeys.",
    },
    careNetwork: [
      {
        role: "Doctor",
        icon: faStethoscope,
        desc: "A clinical view of every woman in your care.",
        chips: [
          { label: "Women's Health", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
      {
        role: "Community Health Worker",
        icon: faHandHoldingHeart,
        desc: "Track and support women in your community.",
        chips: [
          { label: "Women's Health", route: "/scoreplus" },
          { label: "Support", route: "/growthloop" },
        ],
      },
      {
        role: "Pharma Partner",
        icon: faFlaskVial,
        desc: "Aggregate insight across women’s health journeys.",
        chips: [
          { label: "Women's Audience", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
    ],
    closingTitle: "You are the main character.",
    closingSub: "HerLife is built for the woman you are becoming.",
  },

  breathe: {
    ehsaas: {
      emoji: "🫁",
      eyebrow: "Breathe · Daily",
      title: "Air & Ease",
      subtitle:
        "Track symptoms, triggers and the quiet progress of clearer days.",
    },
    care: {
      eyebrow: "LifeNHealth Care Network",
      title: "Everyone Around You",
      subtitle:
        "The clinicians and partners supporting respiratory health.",
    },
    careNetwork: [
      {
        role: "Doctor",
        icon: faStethoscope,
        desc: "A clinical view of every respiratory patient in your care.",
        chips: [
          { label: "Respiratory Cohort", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
      {
        role: "Community Health Worker",
        icon: faHandHoldingHeart,
        desc: "Track and support respiratory health in your community.",
        chips: [
          { label: "Respiratory Cohort", route: "/scoreplus" },
          { label: "Support", route: "/growthloop" },
        ],
      },
      {
        role: "Pharma Partner",
        icon: faFlaskVial,
        desc: "Aggregate insight across the respiratory care journey.",
        chips: [
          { label: "Respiratory Audience", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
    ],
    closingTitle: "Breathe easier, one day at a time.",
    closingSub: "We're keeping every reading safe and clear.",
  },

  sahara: {
    ehsaas: {
      emoji: "🌿",
      eyebrow: "Sahara · Daily",
      title: "Vibrant After 60",
      subtitle:
        "Strength, balance, memory and the joy of staying independent.",
    },
    care: {
      eyebrow: "LifeNHealth Care Network",
      title: "Everyone Around You",
      subtitle:
        "The clinicians and partners supporting healthy ageing.",
    },
    careNetwork: [
      {
        role: "Doctor",
        icon: faStethoscope,
        desc: "A clinical view of every senior in your care.",
        chips: [
          { label: "Senior Cohort", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
      {
        role: "Community Health Worker",
        icon: faHandHoldingHeart,
        desc: "Track and support seniors in your community.",
        chips: [
          { label: "Senior Cohort", route: "/scoreplus" },
          { label: "Support", route: "/growthloop" },
        ],
      },
      {
        role: "Pharma Partner",
        icon: faFlaskVial,
        desc: "Aggregate insight across healthy-ageing journeys.",
        chips: [
          { label: "Senior Audience", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
    ],
    closingTitle: "Age is just a number.",
    closingSub: "Sahara is here so your best years stay full of life.",
  },

  renalife: {
    ehsaas: {
      emoji: "🫘",
      eyebrow: "RenaLife · Daily",
      title: "Kidney Care, Simplified",
      subtitle:
        "Labs, fluid, meds and the everyday habits that protect your filters.",
    },
    care: {
      eyebrow: "LifeNHealth Care Network",
      title: "Everyone Around You",
      subtitle:
        "The clinicians and partners supporting kidney health.",
    },
    careNetwork: [
      {
        role: "Doctor",
        icon: faStethoscope,
        desc: "A clinical view of every kidney patient in your care.",
        chips: [
          { label: "Renal Cohort", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
      {
        role: "Community Health Worker",
        icon: faHandHoldingHeart,
        desc: "Track and support kidney-health journeys in your community.",
        chips: [
          { label: "Renal Cohort", route: "/scoreplus" },
          { label: "Support", route: "/growthloop" },
        ],
      },
      {
        role: "Pharma Partner",
        icon: faFlaskVial,
        desc: "Aggregate insight across the renal care journey.",
        chips: [
          { label: "Renal Audience", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
    ],
    closingTitle: "Gentle care, every day.",
    closingSub: "RenaLife keeps the important numbers close and clear.",
  },

  neurolife: {
    ehsaas: {
      emoji: "🧠",
      eyebrow: "NeuroLife · Daily",
      title: "Mind & Nerve",
      subtitle:
        "Focus, calm, cognition and the quiet work of a steady mind.",
    },
    care: {
      eyebrow: "LifeNHealth Care Network",
      title: "Everyone Around You",
      subtitle:
        "The clinicians and partners supporting neurological health.",
    },
    careNetwork: [
      {
        role: "Doctor",
        icon: faStethoscope,
        desc: "A clinical view of every neurology patient in your care.",
        chips: [
          { label: "Neuro Cohort", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
      {
        role: "Community Health Worker",
        icon: faHandHoldingHeart,
        desc: "Track and support neurological journeys in your community.",
        chips: [
          { label: "Neuro Cohort", route: "/scoreplus" },
          { label: "Support", route: "/growthloop" },
        ],
      },
      {
        role: "Pharma Partner",
        icon: faFlaskVial,
        desc: "Aggregate insight across the neurology care journey.",
        chips: [
          { label: "Neuro Audience", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
    ],
    closingTitle: "Clarity is a practice.",
    closingSub: "NeuroLife walks with you through every cognitive chapter.",
  },

  rhythm: {
    ehsaas: {
      emoji: "💓",
      eyebrow: "Rhythm · Daily",
      title: "Thyroid Balance",
      subtitle:
        "Energy, weight, mood and the quiet conductor of your metabolism.",
    },
    care: {
      eyebrow: "LifeNHealth Care Network",
      title: "Everyone Around You",
      subtitle:
        "The clinicians and partners supporting thyroid health.",
    },
    careNetwork: [
      {
        role: "Doctor",
        icon: faStethoscope,
        desc: "A clinical view of every thyroid patient in your care.",
        chips: [
          { label: "Thyroid Cohort", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
      {
        role: "Community Health Worker",
        icon: faHandHoldingHeart,
        desc: "Track and support thyroid journeys in your community.",
        chips: [
          { label: "Thyroid Cohort", route: "/scoreplus" },
          { label: "Support", route: "/growthloop" },
        ],
      },
      {
        role: "Pharma Partner",
        icon: faFlaskVial,
        desc: "Aggregate insight across the thyroid care journey.",
        chips: [
          { label: "Thyroid Audience", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
    ],
    closingTitle: "Find your rhythm again.",
    closingSub: "We're here for the numbers and the feelings in between.",
  },

  thrive: {
    ehsaas: {
      emoji: "🌈",
      eyebrow: "Thrive · Daily",
      title: "Everyday Wellness",
      subtitle:
        "Small habits that compound into a brighter, steadier you.",
    },
    care: {
      eyebrow: "LifeNHealth Care Network",
      title: "Everyone Around You",
      subtitle:
        "The clinicians and partners supporting everyday wellness.",
    },
    careNetwork: [
      {
        role: "Doctor",
        icon: faStethoscope,
        desc: "A clinical view of every wellness patient in your care.",
        chips: [
          { label: "Wellness Cohort", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
      {
        role: "Community Health Worker",
        icon: faHandHoldingHeart,
        desc: "Track and support wellness journeys in your community.",
        chips: [
          { label: "Wellness Cohort", route: "/scoreplus" },
          { label: "Support", route: "/growthloop" },
        ],
      },
      {
        role: "Pharma Partner",
        icon: faFlaskVial,
        desc: "Aggregate insight across everyday wellness journeys.",
        chips: [
          { label: "Wellness Audience", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
    ],
    closingTitle: "Progress over perfection.",
    closingSub: "Thrive is the gentle system that keeps you moving forward.",
  },

  vibe: {
    ehsaas: {
      emoji: "✨",
      eyebrow: "Vibe · Daily",
      title: "Youth Energy",
      subtitle:
        "Glow, strength, recovery and the freedom to feel unstoppable.",
    },
    care: {
      eyebrow: "LifeNHealth Care Network",
      title: "Everyone Around You",
      subtitle:
        "The clinicians and partners supporting youth health.",
    },
    careNetwork: [
      {
        role: "Doctor",
        icon: faStethoscope,
        desc: "A clinical view of every young person in your care.",
        chips: [
          { label: "Youth Cohort", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
      {
        role: "Community Health Worker",
        icon: faHandHoldingHeart,
        desc: "Track and support youth health in your community.",
        chips: [
          { label: "Youth Cohort", route: "/scoreplus" },
          { label: "Support", route: "/growthloop" },
        ],
      },
      {
        role: "Pharma Partner",
        icon: faFlaskVial,
        desc: "Aggregate insight across youth health journeys.",
        chips: [
          { label: "Youth Audience", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
    ],
    closingTitle: "Keep the vibe high.",
    closingSub: "We're tracking the data so you can stay in the moment.",
  },

  udaan: {
    ehsaas: {
      emoji: "🕊️",
      eyebrow: "Udaan · Daily",
      title: "Growing Strong (5-18)",
      subtitle:
        "For the young minds and bodies learning to take flight.",
    },
    care: {
      eyebrow: "LifeNHealth Care Network",
      title: "Everyone Around Them",
      subtitle:
        "The clinicians and partners supporting children & teens.",
    },
    careNetwork: [
      {
        role: "Doctor",
        icon: faStethoscope,
        desc: "A clinical view of every young person in your care.",
        chips: [
          { label: "Youth Cohort", route: "/scoreplus" },
          { label: "Growth", route: "/growthloop" },
        ],
      },
      {
        role: "Community Health Worker",
        icon: faHandHoldingHeart,
        desc: "Track and support children & teens in your community.",
        chips: [
          { label: "Youth Cohort", route: "/scoreplus" },
          { label: "Support", route: "/growthloop" },
        ],
      },
      {
        role: "Pharma Partner",
        icon: faFlaskVial,
        desc: "Aggregate insight across youth health journeys.",
        chips: [
          { label: "Youth Audience", route: "/scoreplus" },
          { label: "Early Years", route: "/growthloop" },
        ],
      },
    ],
    closingTitle: "Ready for take-off.",
    closingSub: "Udaan grows with every young person we support.",
  },

  hopelife: {
    ehsaas: {
      emoji: "🎗️",
      eyebrow: "HopeLife · Daily",
      title: "Courage & Care",
      subtitle:
        "Symptoms, treatments, side-effects and the quiet strength of hope.",
    },
    care: {
      eyebrow: "LifeNHealth Care Network",
      title: "Everyone Around You",
      subtitle:
        "The clinicians and partners walking the oncology journey with you.",
    },
    careNetwork: [
      {
        role: "Doctor",
        icon: faStethoscope,
        desc: "A clinical view of every oncology patient in your care.",
        chips: [
          { label: "Oncology Cohort", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
      {
        role: "Community Health Worker",
        icon: faHandHoldingHeart,
        desc: "Track and support oncology journeys in your community.",
        chips: [
          { label: "Oncology Cohort", route: "/scoreplus" },
          { label: "Support", route: "/growthloop" },
        ],
      },
      {
        role: "Pharma Partner",
        icon: faFlaskVial,
        desc: "Aggregate insight across the oncology care journey.",
        chips: [
          { label: "Oncology Audience", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
    ],
    closingTitle: "Hope is a practice too.",
    closingSub: "We're holding space for every hard and beautiful day.",
  },

  podia: {
    ehsaas: {
      emoji: "🦶",
      eyebrow: "Podia · Daily",
      title: "Feet First",
      subtitle:
        "Comfort, mobility and the foundation of every step you take.",
    },
    care: {
      eyebrow: "LifeNHealth Care Network",
      title: "Everyone Around You",
      subtitle:
        "The clinicians and partners supporting foot health.",
    },
    careNetwork: [
      {
        role: "Doctor",
        icon: faStethoscope,
        desc: "A clinical view of every foot-health patient in your care.",
        chips: [
          { label: "Podiatry Cohort", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
      {
        role: "Community Health Worker",
        icon: faHandHoldingHeart,
        desc: "Track and support foot-health journeys in your community.",
        chips: [
          { label: "Podiatry Cohort", route: "/scoreplus" },
          { label: "Support", route: "/growthloop" },
        ],
      },
      {
        role: "Pharma Partner",
        icon: faFlaskVial,
        desc: "Aggregate insight across the foot-health journey.",
        chips: [
          { label: "Podiatry Audience", route: "/scoreplus" },
          { label: "Insights", route: "/growthloop" },
        ],
      },
    ],
    closingTitle: "Strong foundations matter.",
    closingSub: "Podia keeps every step tracked and supported.",
  },
};

/* ------------------------------------------------------------------ */
/*  Building blocks (theme-aware)                                      */
/* ------------------------------------------------------------------ */
function SectionHeading({ eyebrow, title, subtitle, emoji, theme }) {
  return (
    <div className="mb-4 sm:mb-5">
      <span
        className="font-mono text-[0.65rem] tracking-[0.22em] uppercase opacity-90"
        style={{ color: theme.accent }}
      >
        {eyebrow}
      </span>
      <h2
        className="mt-1 flex items-center gap-2 font-display text-[1.35rem] sm:text-[1.6rem] font-semibold leading-tight"
        style={{ color: theme.textPrimary }}
      >
        <span aria-hidden="true">{emoji}</span>
        {title}
      </h2>
      <p
        className="mt-1 text-[0.8rem] sm:text-[0.86rem]"
        style={{ color: theme.textSecondary }}
      >
        {subtitle}
      </p>
    </div>
  );
}

function NavTile({ item, theme, onNavigate }) {
  return (
    <button
      type="button"
      onClick={() => onNavigate(item.route)}
      className="group relative flex flex-col items-start gap-2.5 rounded-2xl border backdrop-blur-md px-3.5 py-3.5 text-left
        shadow-[0_4px_18px_-6px_rgba(0,0,0,0.08)]
        transition-all duration-200 ease-out
        active:scale-[0.97] motion-safe:hover:-translate-y-0.5
        focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
        min-h-[92px] w-full"
      style={{
        borderColor: `${theme.accentBorder}66`,
        backgroundColor: "rgba(255,255,255,0.7)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = theme.accentSoft;
        e.currentTarget.style.borderColor = `${theme.accentBorder}b3`;
        e.currentTarget.style.boxShadow = `0 10px 28px -8px ${theme.glow}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.7)";
        e.currentTarget.style.borderColor = `${theme.accentBorder}66`;
        e.currentTarget.style.boxShadow =
          "0 4px 18px -6px rgba(0,0,0,0.08)";
      }}
    >
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
        style={{ backgroundColor: theme.accentSoft, color: theme.accent }}
      >
        <FontAwesomeIcon
          icon={item.icon}
          className="w-[18px] h-[18px]"
          aria-hidden="true"
        />
      </span>
      <span className="min-w-0">
        <span
          className="block text-[0.8rem] font-semibold leading-tight"
          style={{ color: theme.textPrimary }}
        >
          {item.label}
        </span>
        <span
          className="block text-[0.68rem] leading-snug mt-0.5"
          style={{ color: theme.textSecondary }}
        >
          {item.desc}
        </span>
      </span>
      <FontAwesomeIcon
        icon={faChevronRight}
        className="absolute right-3 top-3.5 w-[12px] h-[12px] opacity-50 group-hover:opacity-80 transition-opacity"
        style={{ color: theme.accent }}
        aria-hidden="true"
      />
    </button>
  );
}

function NavGrid({ items, theme, onNavigate }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5 sm:gap-3">
      {items.map((item) => (
        <NavTile
          key={item.label + item.route}
          item={item}
          theme={theme}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );
}

function CareCard({ entry, theme, onNavigate }) {
  return (
    <div
      className="rounded-2xl border backdrop-blur-md px-4 py-4 sm:px-5 sm:py-5 flex flex-col gap-3 shadow-[0_4px_18px_-6px_rgba(0,0,0,0.08)]"
      style={{
        borderColor: `${theme.accentBorder}66`,
        backgroundColor: "rgba(255,255,255,0.7)",
      }}
    >
      <div className="flex items-center gap-3">
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
          style={{ backgroundColor: theme.accentSoft, color: theme.accent }}
        >
          <FontAwesomeIcon
            icon={entry.icon}
            className="w-[19px] h-[19px]"
            aria-hidden="true"
          />
        </span>
        <h3
          className="font-display text-[1rem] font-semibold"
          style={{ color: theme.textPrimary }}
        >
          {entry.role}
        </h3>
      </div>
      <p
        className="text-[0.78rem] leading-relaxed"
        style={{ color: theme.textSecondary }}
      >
        {entry.desc}
      </p>
      <div className="flex flex-wrap gap-2 mt-1">
        {entry.chips.map((chip) => (
          <button
            key={chip.label + chip.route}
            type="button"
            onClick={() => onNavigate(chip.route)}
            className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[0.7rem] font-medium
              active:scale-[0.96] transition-all
              focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            style={{
              borderColor: `${theme.accentBorder}80`,
              backgroundColor: `${theme.accentSoft}cc`,
              color: theme.textSecondary,
            }}
          >
            {chip.label}
            <FontAwesomeIcon
              icon={faChevronRight}
              className="w-[10px] h-[10px]"
              aria-hidden="true"
            />
          </button>
        ))}
      </div>
    </div>
  );
}

function ThreadNode({ emoji, theme }) {
  return (
    <div className="relative flex sm:flex-col items-center sm:h-full">
      <span
        className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full text-[0.9rem] shrink-0"
        style={{
          backgroundColor: theme.accentBorder,
          boxShadow: `0 0 16px ${theme.glow}`,
        }}
        aria-hidden="true"
      >
        {emoji}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main                                                               */
/* ------------------------------------------------------------------ */
export default function LifeNHealth({ navigate }) {
  const [selectedId, setSelectedId] = useState("maternity");

  const selected =
    PRODUCTS.find((p) => p.id === selectedId) ?? PRODUCTS[0];
  const theme = selected.theme;

  const copy = PRODUCT_COPY[selectedId] || PRODUCT_COPY.maternity;

  const showGrowthSection =
    selectedId === "maternity" || selectedId === "childgrowth";

  const go = (route) => {
    if (navigate) navigate(route);
    else if (typeof window !== "undefined") window.location.href = route;
  };

  return (
    <div
      className="relative w-full min-h-[100dvh] overflow-x-hidden font-sans transition-colors duration-500"
      style={{
        background: `linear-gradient(to bottom, ${theme.pageFrom}, ${theme.pageVia}, ${theme.pageTo})`,
        color: theme.textPrimary,
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&display=swap');
        .font-display { font-family: 'Fraunces', Georgia, serif; }
      `}</style>

      {/* Ambient blobs */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute -top-24 -left-16 h-72 w-72 rounded-full blur-[110px] opacity-30"
          style={{ backgroundColor: theme.accentBorder }}
        />
        <div
          className="absolute top-[30%] -right-20 h-80 w-80 rounded-full blur-[120px] opacity-25"
          style={{ backgroundColor: theme.headerVia }}
        />
        <div
          className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full blur-[120px] opacity-25"
          style={{ backgroundColor: theme.headerTo }}
        />
      </div>

      <LifeNHealthHeaderBanner
        selectedId={selectedId}
        onSelect={setSelectedId}
      />

      <main className="relative z-10 px-4 sm:px-6 pb-10 max-w-3xl mx-auto">
        {/* ── Ehsaas / Daily section (always present) ── */}
        <section className="flex gap-3 sm:gap-4">
          <div className="flex flex-col items-center pt-1">
            <ThreadNode emoji={copy.ehsaas.emoji} theme={theme} />
            {showGrowthSection && (
              <span
                className="w-px flex-1 mt-1"
                style={{
                  background: `linear-gradient(to bottom, ${theme.accentBorder}80, ${theme.accentBorder}40)`,
                }}
              />
            )}
          </div>
          <div className="flex-1 pb-8">
            <SectionHeading
              eyebrow={copy.ehsaas.eyebrow}
              title={copy.ehsaas.title}
              subtitle={copy.ehsaas.subtitle}
              emoji={copy.ehsaas.emoji}
              theme={theme}
            />
            <NavGrid items={EHSAAS_ITEMS} theme={theme} onNavigate={go} />
          </div>
        </section>

        {/* ── Growth section (only for Maternity + ChildGrowth) ── */}
        {showGrowthSection && (
          <section className="flex gap-3 sm:gap-4">
            <div className="flex flex-col items-center pt-1">
              <ThreadNode emoji={copy.growth.emoji} theme={theme} />
              <span
                className="w-px flex-1 mt-1"
                style={{
                  background: `linear-gradient(to bottom, ${theme.accentBorder}80, ${theme.accentBorder}40)`,
                }}
              />
            </div>
            <div className="flex-1 pb-8">
              <SectionHeading
                eyebrow={copy.growth.eyebrow}
                title={copy.growth.title}
                subtitle={copy.growth.subtitle}
                emoji={copy.growth.emoji}
                theme={theme}
              />
              <NavGrid items={GROWTH_ITEMS} theme={theme} onNavigate={go} />
            </div>
          </section>
        )}

        {/* ── Care Network (always present) ── */}
        <section className="flex gap-3 sm:gap-4">
          <div className="flex flex-col items-center pt-1">
            <ThreadNode emoji="🤝" theme={theme} />
          </div>
          <div className="flex-1 pb-4">
            <SectionHeading
              eyebrow={copy.care.eyebrow}
              title={copy.care.title}
              subtitle={copy.care.subtitle}
              emoji="🤝"
              theme={theme}
            />
            <div className="flex flex-col gap-3 sm:grid sm:grid-cols-3">
              {copy.careNetwork.map((entry) => (
                <CareCard
                  key={entry.role}
                  entry={entry}
                  theme={theme}
                  onNavigate={go}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Closing */}
        <div
          className="mt-2 flex items-start gap-2.5 px-4 py-3.5 sm:px-5 sm:py-4 rounded-2xl border backdrop-blur-md shadow-[0_4px_18px_-6px_rgba(0,0,0,0.08)]"
          style={{
            borderColor: `${theme.accentBorder}59`,
            backgroundColor: "rgba(255,255,255,0.7)",
          }}
        >
          <FontAwesomeIcon
            icon={faWandMagicSparkles}
            className="w-[16px] h-[16px] shrink-0 mt-0.5"
            style={{ color: theme.accent }}
            aria-hidden="true"
          />
          <p
            className="text-[0.85rem] sm:text-[0.88rem] font-medium leading-snug"
            style={{ color: theme.textPrimary }}
          >
            {copy.closingTitle}
            <br />
            <span
              className="text-[0.76rem] font-normal"
              style={{ color: theme.textSecondary }}
            >
              {copy.closingSub}
            </span>
          </p>
        </div>

        {/* Local Economy ecosystem links */}
        <div className="mt-6 flex flex-col items-center gap-3">
          <p
            className="text-[0.65rem] font-mono uppercase tracking-[0.18em] opacity-90"
            style={{ color: theme.accent }}
          >
            Connected to Pados / Neighbourhood / Local Economy
          </p>

          <div className="flex flex-wrap items-center justify-center gap-2.5">
            {[
              { label: "FindItLocal", emoji: "📍", route: "/finditlocal" },
              { label: "FoundHer", emoji: "🌸", route: "/foundher" },
              { label: "FiLTV", emoji: "📺", route: "/filtv" },
            ].map((item) => (
              <button
                key={item.route}
                type="button"
                onClick={() => go(item.route)}
                className="inline-flex items-center gap-1.5 rounded-full border w-fit px-3.5 py-1.5 text-[0.72rem] font-medium
          backdrop-blur-sm transition-all duration-200
          hover:scale-[1.03] active:scale-[0.97]
          focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                style={{
                  borderColor: `${theme.accentBorder}80`,
                  backgroundColor: "rgba(255,255,255,0.65)",
                  color: theme.textPrimary,
                }}
              >
                <span className="text-[0.9rem]" aria-hidden="true">
                  {item.emoji}
                </span>
                {item.label}
              </button>
            ))}
          </div>
        </div>
        <p
          className="mt-6 text-center text-[0.68rem] font-mono tracking-wide opacity-70"
          style={{ color: theme.accent }}
        >
          THINK5WHYS · LIFENHEALTH
        </p>
      </main>
    </div>
  );
}